import { Controller } from "stimulus";

export default class extends Controller {
  static targets = ["errorPanel", "buttonHint", "spinner", "formWrapper"];

  connect() {
    this.initForm();
    this.updateErrorView();
  }

  disconnect() {
    this.bancardForm.config.onSuccess = () => {};
    this.bancardForm.config.onError = () => {};
    this.bancardForm.iframe.onload = () => {};
    this.bancardForm = null;
  }

  get bancardPublicKey() {
    return this.data.get("bancard-public-key");
  }

  get brandColour() {
    return this.data.get("brand-colour");
  }

  get error() {
    return this.data.get("error");
  }

  set error(error) {
    this.data.set("error", error);
    this.publishPurchaseErrorUpdatedEvent();
    this.updateErrorView();
  }

  get enableCaptcha() {
    return this.data.get("bancard-enable-captcha") === "true";
  }

  initForm() {
    let self = this;
    this.bancardForm = window.HostedForm({
      apiKey: this.bancardPublicKey,
      id: "#bancard-payment-form",
      showCaptcha: this.enableCaptcha,
      formSelector: "#bancard-form",
      autoSubmit: false,
      showSecurityCode: true,
      disableAfterSubmit: true,
      showLabels: true,
      showMessages: true,
      buttonGroupAlignment: "left",
      placeholders: {
        cardName: "Cardholder name",
        cardNumber: "Card number",
        cardZip: "Zip/Postal Code",
        cardExpiry: "MM/YY",
        cardSecurity: "CVC",
      },
      onSuccess: (result) => self.showSuccess(result),
      onError: (result) => self.showErrors(result),
      styles: {
        base: {
          paddingBotton: "140px",
          paddingTop: "30px",
          fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
        },
        form: {
          color: "#FFFFFF",
        },
        label: {
          fontSize: "16px",
          lineHeight: "18px",
          fontWeight: "300",
          color: "#505E75",
          margin: "10px 0 10px 0",
          display: "block",
        },
        input: {
          display: "block",
          border: "1px solid #A2ADC0",
          padding: "10px 12px 11px 12px",
          borderRadius: "4px",
          width: "100%",
          margin: "0 0 15px 0",
          fontSize: "16px",
          lineHeight: "18px",
          color: "#505E75",
          "::-webkit-input-placeholder": { fontStyle: "italic" },
          "::-moz-placeholder": { fontStyle: "italic" },
          ":-ms-input-placeholder": { fontStyle: "italic" },
          ":-moz-placeholder": { fontStyle: "italic" },
          ":focus": {
            outline: "none",
            boxShadow: "0 0 0 3px rgba(66,153,225,0.5)",
          },
        },
        button: {
          backgroundColor: this.brandColour,
          color: "#fff",
          borderRadius: "20px",
          fontSize: "18px",
          fontWeight: "500",
          display: "block",
          margin: "43px 24vw 8px 22vw",
          width: "56vw", //'239px' when the iframe has the size of container.
          height: "40px",
          boxShadow: `${this.brandColour} 0px 2px 4px 0px`,
          ":focus": {
            outline: "none",
            boxShadow: "0 0 0 3px rgba(66,153,225,0.5)",
          },
          ":active": {
            outline: "none",
            boxShadow: "0 0 0 3px rgba(66,153,225,0.5)",
          },
        },
        helpError: {
          fontSize: "12px",
          lineHeight: "12px",
          color: "#E74C3C",
          margin: "2px 0px 0 0px",
        },
      },
    });
    this.bancardForm.mount();
    this.bancardForm.iframe.onload = () => self.bancardLoaded();
  }

  bancardLoaded() {
    this.hideSpinner();
  }

  hideSpinner() {
    let self = this;
    $(this.spinnerTarget).fadeOut("slow", () => {
      $(self.buttonHintTarget).removeClass("invisible");
      $(self.buttonHintTarget).addClass("opacity-100");
      $(self.formWrapperTarget).removeClass("invisible");
      $(self.formWrapperTarget).addClass("opacity-100");
    });
  }

  showSpinner() {
    $(this.buttonHintTarget).addClass("invisible");
    $(this.buttonHintTarget).removeClass("opacity-100");
    $(this.formWrapperTarget).addClass("invisible");
    $(this.formWrapperTarget).removeClass("opacity-100");
    $(this.spinnerTarget).fadeIn(100);
  }

  showSuccess(result) {
    this.error = "";
    this.createPurchase(result.token, result.securityCode);
  }

  showErrors(result) {
    this.bancardForm.enableForm();
    const bancardErrorMessage = result.message;
    console.log(`Bancard form error: ${bancardErrorMessage}`, this.element);
    switch (bancardErrorMessage) {
      case "LUHN/MOD10 check on account number failed":
        this.error =
          "Could not process card. Please check that the card details are correct.";
        break;
      case "Expdate not in proper format":
      case "Invalid expdate":
        this.error = "Expiry date is invalid.";
        break;
      case "Unable to determine cardtype":
        this.error = "Card number is invalid.";
        break;
      case "INVALID ZIPCODE. NOT VALID FOR US OR CANADA.":
        this.error = "Zipcode is invalid.";
        break;
      case "The given data was invalid.":
        this.error = "";
        break;
      default:
        this.error = bancardErrorMessage;
    }
  }

  createPurchase(token, cvc) {
    let self = this;
    this.showSpinner();
    this.data.set("error", "");
    this.updateErrorView();

    let data = {
      business_id: this.data.get("business-id"),
      branch_id: this.data.get("branch-id"),
      domain_name: this.data.get("domain-name"),
      amount: this.data.get("amount"),
      merchant_account_ref: this.data.get("merchant-account-ref"),
      token: token,
      cvc: cvc,
      purchase_number: this.data.get("purchase-number"),
      purchaser_email: this.data.get("purchaser-email"),
      recipient_email: this.optionSpecificRecipientEmail,
      purchaser_first_name: this.data.get("purchaser-first-name") || null,
      purchaser_last_name: this.data.get("purchaser-last-name") || null,
      recipient_message: this.data.get("recipient-message") || null,
      send_option: this.data.get("send-option"),
      payment_provider: "bancard",
    };

    $.ajax({
      type: "post",
      url: `${window.location.pathname}/purchases`,
      contentType: "application/json; charset=utf-8",
      dataType: "json",
      data: JSON.stringify(data),
      success: (result) => {
        console.log(result, self.element);
        this.publishPaymentConfirmedEvent();
      },
      error: this.handlePurchaseCreationError.bind(this),
    });
  }

  get optionSpecificRecipientEmail() {
    if (this.data.get("send-option") == "SEND_TO_MYSELF") {
      return this.data.get("purchaser-email");
    } else {
      return this.data.get("recipient-email");
    }
  }

  handlePurchaseCreationError(response) {
    this.data.set("error", response.responseJSON.message);
    this.updateErrorView();
    this.bancardForm.enableForm();
    this.hideSpinner();
  }

  publishPaymentConfirmedEvent() {
    const event = document.createEvent("CustomEvent");
    event.initCustomEvent("payment-confirmed", true, true, null);
    this.element.dispatchEvent(event);
  }

  publishPurchaseErrorUpdatedEvent() {
    const event = document.createEvent("CustomEvent");
    event.initCustomEvent("purchase-error-updated", true, true, null);
    event.purchaseError = this.error;
    this.element.dispatchEvent(event);
  }

  updateErrorView() {
    this.errorPanelTarget.innerHTML = this.error;
    this.errorPanelTarget.classList.toggle("hidden", this.error === "");
  }
}
